import React, { useState, useRef, useEffect } from "react";
import { registerLicense } from "@syncfusion/ej2-base";
import {
  GanttComponent,
  ColumnsDirective,
  ColumnDirective,
  EventMarkersDirective,
  EventMarkerDirective,
  Inject,
  Selection,
  Sort,
  Filter,
  DayMarkers,
  Resize,
  Toolbar,
  ExcelExport,
  PdfExport,
  Edit,
} from "@syncfusion/ej2-react-gantt";
import { SwitchComponent } from "@syncfusion/ej2-react-buttons";
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import useStartupStore from "../stores/startupStore";
import { IconArrowLeft } from "@tabler/icons-react";
import styled from "styled-components";
// Register Syncfusion license
registerLicense(
  "ORg4AjUWIQA/Gnt2XVhhQlJHfV5AQmBIYVp/TGpJfl96cVxMZVVBJAtUQF1hTH5RdEBjUXpYcndVRmlc"
);

const BackButton = styled.button`
  @media(min-width: 768px){
    display: none;
  }
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #6c45e7;
  font-size: 12px;
  font-weight: 500;
  padding: 8px 0;
  cursor: pointer;
  margin-bottom: 12px;

  &:active {
    opacity: 0.7;
  }
`;

// Status mapping
const STATUS_MAPPING = {
  Open: 0,
  InProgress: 40,
  Review: 70,
  Close: 100,
};

// Status Colors Mapping
const statusColors = {
  Open: "#0251cc",      // Blue
  InProgress: "#ea9713", // Orange
  Review: "#8e4399",     // Purple
  Close: "#63ba3c",      // Green
};

function parseAndAdjustDate(dateString, isEndDate = false) {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
      console.error(`Failed to parse date string: ${dateString}`);
      return null;
  }
  return date;
}

function transformProgramToTasks(program) {
  if (!program || program.loading || !program.categories) return [];
  const validCategories = program.categories.filter(
    (category) => parseAndAdjustDate(category.start_date) && parseAndAdjustDate(category.end_date)
  );
  return validCategories.map((category) => {
    const categoryStartDate = parseAndAdjustDate(category.start_date, false);
    const categoryEndDate = parseAndAdjustDate(category.end_date, true);
    const usersArray = category.assigned_users || category.assignedUsers || [];
    const resources =
      usersArray.length > 0
        ? usersArray.map((user) => user.user.first_name).join(", ")
        : "";
        
    // Fixed: Properly filter valid subcategories
    const validSubCategories = (category.sub_categories || []).filter(
      (sub) => parseAndAdjustDate(sub.start_date || sub.start_day) && 
                parseAndAdjustDate(sub.end_date || sub.end_day)
    );

    
    return {
      TaskID: category.id,
      TaskName: category.name,
      StartDate: categoryStartDate,
      EndDate: categoryEndDate,
      AssignedUsers: resources,
      subtasks: validSubCategories.map((sub) => {
        const subStartDate = parseAndAdjustDate(sub.start_date || sub.start_day, false);
        const subEndDate = parseAndAdjustDate(sub.end_date || sub.end_day, true);
        const progressValue = sub.status ? STATUS_MAPPING[sub.status] || 0 : 0;
        const usersArray2 = sub.assigned_users || sub.assignedUsers || [];
        const resources2 =
          usersArray2.length > 0
            ? usersArray2.map((user) => user.user.first_name).join(", ")
            : "No users assigned";
        return {
          TaskID: sub.id,
          TaskName: sub.name,
          StartDate: subStartDate,
          EndDate: subEndDate,
          Progress: progressValue,
          AssignedUsers: resources2,
          Status: sub.status, // Make sure Status is properly set
        };
      }),
    };
  });
}

// Add this function to preprocess your task data to ensure explicit durations
function preprocessTasksWithExplicitDurations(tasks) {
  return tasks.map(task => {
    const taskCopy = {...task};
    
    // For parent tasks, ensure duration is explicitly calculated
    if (taskCopy.subtasks && taskCopy.subtasks.length > 0) {
      // Ensure start and end dates are Date objects
      const startDate = new Date(taskCopy.StartDate);
      const endDate = new Date(taskCopy.EndDate);
      
      // Calculate duration in days between start and end date
      // Add 1 to include both start and end days
      const durationInDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      
      // Set the duration explicitly
      taskCopy.Duration = durationInDays;
      
      // Ensure explicit progress value for parent tasks (optional)
      if (typeof taskCopy.Progress === 'undefined') {
        // Calculate average progress from subtasks or set a default
        const subtaskProgress = taskCopy.subtasks
          .map(sub => sub.Progress || 0)
          .reduce((sum, progress) => sum + progress, 0);
        
        taskCopy.Progress = taskCopy.subtasks.length > 0 
          ? subtaskProgress / taskCopy.subtasks.length 
          : 0;
      }
      
      // Process subtasks recursively
      taskCopy.subtasks = preprocessTasksWithExplicitDurations(taskCopy.subtasks);
    }
    
    return taskCopy;
  });
}

function computeProjectDates(program) {
  if (!program || !program.categories || program.categories.length === 0) {
    return { projectStartDate: null, projectEndDate: null };
  }
  const validCategories = program.categories.filter(
    (category) => parseAndAdjustDate(category.start_date) && parseAndAdjustDate(category.end_date)
  );
  if (validCategories.length === 0) {
    return { projectStartDate: null, projectEndDate: null };
  }
  const startTimes = validCategories.map((cat) => parseAndAdjustDate(cat.start_date)?.getTime()).filter(time => time !== null);
  const endTimes = validCategories.map((cat) => parseAndAdjustDate(cat.end_date, true)?.getTime()).filter(time => time !== null);
  if (startTimes.length === 0 || endTimes.length === 0) {
       return { projectStartDate: null, projectEndDate: null };
  }
  const earliestStart = new Date(Math.min(...startTimes));
  const latestEnd = new Date(Math.max(...endTimes));
  const oneWeek = 7 * 24 * 60 * 60 * 1000;
  const paddedStartDate = new Date(earliestStart.getTime() - oneWeek);
  const paddedEndDate = new Date(latestEnd.getTime() + oneWeek);
  return {
    projectStartDate: isNaN(paddedStartDate.getTime()) ? null : paddedStartDate,
    projectEndDate: isNaN(paddedEndDate.getTime()) ? null : paddedEndDate,
  };
}

// Static Gantt Settings
const oldCode_gridLines = "Both";
const oldCode_timelineSettings = {
  topTier: { unit: "Month", format: "MMM yy", height: 50 },
  bottomTier: { unit: "Day", format: "EEE", height: 30 },
};
const oldCode_labelSettings = {
  leftLabel: "TaskName",
  rightLabel: "AssignedUsers",
};
const oldCode_ganttHeight = "700px";
const oldCode_rowHeight = 40;
const oldCode_taskbarHeight = 30;

function ProgramGantt({onBack}) {
  const { program, fetchProgram } = useStartupStore((state) => ({
    program: state.program,
    fetchProgram: state.fetchProgram,
  }));
  const ganttRef = useRef(null);
  const [isFitToWidth, setIsFitToWidth] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [projectDates, setProjectDates] = useState({
    projectStartDate: null,
    projectEndDate: null,
  });
  const [hasComputedGanttData, setHasComputedGanttData] = useState(false);

  useEffect(() => {
    if (program && !program.loading) {
      let transformedTasks = transformProgramToTasks(program);
      
      // Preprocess tasks to ensure explicit durations on parent tasks
      transformedTasks = preprocessTasksWithExplicitDurations(transformedTasks);
      
      setTasks(transformedTasks);
      const dates = computeProjectDates(program);
      setProjectDates(dates);
      setHasComputedGanttData(true);
    }
  }, [program]);

  const handleAutofitChange = (args) => {
    setIsFitToWidth(args.checked);
  };
  
  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      if (fetchProgram && typeof fetchProgram === 'function') {
         await fetchProgram();
      } else {
          console.warn("fetchProgram action not available in store.");
          if (ganttRef.current) {
             ganttRef.current.refresh();
           }
      }
    } catch (error) {
      console.error('Failed to refresh program data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };
  
  const toolbarClick = (args) => {
    if (!ganttRef.current) return;
    const exportOptions = {
      fitToWidthSettings: { isFitToWidth: isFitToWidth },
      fileName: 'ProgramGantt.pdf'
    };
    const baseId = ganttRef.current.element?.id || "ProgramGantt";
    switch (args.item.id) {
      case `${baseId}_excelexport`:
        exportOptions.fileName = 'ProgramGantt.xlsx';
        ganttRef.current.excelExport(exportOptions);
        break;
      case `${baseId}_csvexport`:
         exportOptions.fileName = 'ProgramGantt.csv';
        ganttRef.current.csvExport(exportOptions);
        break;
      case `${baseId}_pdfexport`:
        ganttRef.current.pdfExport(exportOptions);
        break;
      default:
        break;
    }
  };

  // Custom parent taskbar template
  const parentTaskbarTemplate = (props) => {
    if (!props.ganttProperties) return null;
    
    const task = props.ganttProperties.data;
    if (!task) return null;
    
    return (
      <div className="e-gantt-parent-taskbar" style={{
        backgroundColor: "#666666",
        height: "100%",
        width: "100%",
        borderRadius: "3px",
        border: "1px solid #888888"
      }}>
        {/* You can add custom content inside if needed */}
      </div>
    );
  };

  // Updated queryTaskbarInfo function to force parent tasks to use their own dates
  const queryTaskbarInfo = (args) => {
    // Force parent tasks to use their own start and end dates rather than subtask calculations
    if (args.data && args.data.hasChildRecords) {
      // Get the task's own start and end dates directly from the data
      const taskStartDate = new Date(args.data.StartDate);
      const taskEndDate = new Date(args.data.EndDate);
      
      // Override the taskbar position calculation
      args.taskbarStartDate = taskStartDate;
      args.taskbarEndDate = taskEndDate;
      
      // Disable the default behavior that calculates parent taskbar
      // dimensions based on child tasks
      args.hasChildRecords = false; // Temporarily fool the renderer
      
      // After rendering is done, restore the flag
      setTimeout(() => {
        args.data.hasChildRecords = true;
      }, 0);
      
      // Style for parent tasks
      args.taskbarBgColor = "#666666";  // Medium gray for parent tasks
      args.progressBarBgColor = "#444444"; // Darker gray for progress
      args.taskbarBorderColor = "#888888"; // Light gray border
    } 
    // For subtasks (that have a Status), apply the status-specific color
    else if (args.data && args.data.Status && statusColors[args.data.Status]) {
      args.taskbarBgColor = statusColors[args.data.Status];
      args.progressBarBgColor = statusColors[args.data.Status];
    }
    
    // Return the modified arguments
    return args;
  };

  if (!program || program.loading) {
    return <div className="loading-container">Loading program data...</div>;
  }
  
  if (
    hasComputedGanttData &&
    (tasks.length === 0 ||
      !projectDates.projectStartDate ||
      !projectDates.projectEndDate)
  ) {
    return (
      <div className="no-data-container">
        No valid program data with start and end dates available to display on Gantt.
         <ButtonComponent onClick={handleRefresh} cssClass="e-info" disabled={isRefreshing}>
           {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
         </ButtonComponent>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: "10vh" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px",
        }}
      >
        <BackButton onClick={onBack}>
          <IconArrowLeft size={12} />
          Back to Editor
        </BackButton>
        <div className="hidden md:flex items-center gap-3">
          <label
            htmlFor="autofit"
            className="text-sm font-medium"
          >
            AutoFit in Pdf Export
          </label>
          <SwitchComponent
            id="autofit"
            checked={isFitToWidth}
            change={handleAutofitChange}
          />
          <ButtonComponent
          onClick={handleRefresh}
          disabled={isRefreshing}
          cssClass={isRefreshing ? "e-info e-disabled" : "e-info"}
          iconCss={
            isRefreshing ? "e-icons e-refresh e-spin" : "e-icons e-refresh"
          }
        >
          {isRefreshing ? "Refreshing..." : "Refresh Data"}
        </ButtonComponent>
        </div>
        
      </div>

      <GanttComponent
        id="ProgramGantt"
        ref={ganttRef}
        dataSource={tasks}
        taskFields={{
          id: "TaskID",
          name: "TaskName",
          startDate: "StartDate",
          endDate: "EndDate",
          duration: "Duration",
          progress: "Progress",
          child: "subtasks",
          resources: "AssignedUsers",
        }}
        queryTaskbarInfo={queryTaskbarInfo}
        parentTaskbarTemplate={parentTaskbarTemplate}
        enableMultiTaskbar={false}
        // height={oldCode_ganttHeight}
        treeColumnIndex={0}
        timelineSettings={oldCode_timelineSettings}
        labelSettings={oldCode_labelSettings}
        gridLines={oldCode_gridLines}
        rowHeight={oldCode_rowHeight}
        taskbarHeight={oldCode_taskbarHeight}
        highlightWeekends={true}
        enableAutoFill={false}
        projectStartDate={projectDates.projectStartDate}
        projectEndDate={projectDates.projectEndDate}
        allowSelection={true}
        allowSorting={true}
        allowFiltering={true}
        allowResizing={true}
        toolbar={["ExcelExport", "CsvExport", "PdfExport"]}
        toolbarClick={toolbarClick}
        allowExcelExport={true}
        allowPdfExport={true}
        allowTaskbarOverlap={true}
        taskMode="Manual"
        enablePredecessorValidation={false}
        dayWorkingTime={[{ from: 0, to: 24 }]}
        // Additional settings to prevent automatic parent task scheduling
        scheduleSettings={{
          scheduleStartDate: projectDates.projectStartDate,
          scheduleEndDate: projectDates.projectEndDate,
          autoScheduling: false,
          updateTimescaleView: false,
          baselineColor: "transparent",
        }}
      >
        <ColumnsDirective>
          <ColumnDirective field="TaskName" width="250" headerText="Roadmaps" />
          <ColumnDirective
            field="StartDate"
            width="100"
            headerText="Start Date"
          />
          <ColumnDirective field="EndDate" width="100" headerText="End Date" />
          <ColumnDirective field="Duration" width="100" headerText="Duration" />
          <ColumnDirective field="Progress" width="100" headerText="Progress" />
          <ColumnDirective field="Status" width="100" headerText="Status" />
          <ColumnDirective
            field="AssignedUsers"
            width="150"
            headerText="Assigned Users"
          />
        </ColumnsDirective>

        <EventMarkersDirective>
          {program.categories &&
            program.categories
              .filter(
                (category, index) =>
                  category.start_date && category.end_date && index % 2 === 0
              )
              .map((category) => {
                const markerDate = parseAndAdjustDate(category.end_date, true);
                if (markerDate && !isNaN(markerDate.getTime())) {
                  return (
                    <EventMarkerDirective
                      key={category.id}
                      day={markerDate}
                      label={`${category.name} End`}
                    />
                  );
                }
                return null;
              })}
        </EventMarkersDirective>

        <Inject
          services={[
            Selection,
            Sort,
            Filter,
            DayMarkers,
            Resize,
            Toolbar,
            ExcelExport,
            PdfExport,
            Edit, // Including Edit for better functionality
          ]}
        />
      </GanttComponent>
    </div>
  );
}

export default ProgramGantt;